/**
 * <collection-list-table> - the order table of sections/featured-collection-list.liquid.
 *
 * Every row is server-rendered (snippets/collection-list-row.liquid); this element never builds row markup. All
 * row controls are handled by delegated listeners on the element itself, so rows added later by the search behave
 * the same and no row ever carries a listener of its own.
 *
 *   - Quantity  The -/+ buttons step by the input's step, clamped to its min/max (quantity rule, stock on hand).
 *   - Cart      Add to cart sends the row's variant and quantity through CartAPI, then opens the cart drawer.
 *   - Search    Filters rows by their data-search text (title, SKU, vendor). The first search of a paginated
 *               collection fetches every other page once through the Section Rendering API (?section_id=&page=)
 *               and appends those rows, hidden; only parsed markup is imported, no script is run. Clearing the
 *               search restores the current page and its pagination.
 *   - Zoom      A thumbnail opens its larger image in the section's <es-modal>.
 *
 * Theme Editor: re-rendering the section disconnects this element, which aborts any page fetches still running.
 */
class CollectionListTable extends HTMLElement {
  static FETCH_CONCURRENCY = 4;
  static SEARCH_DELAY = 250;

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
    this.searchTimer = null;
    this.abortController = null;
    this.pagesPromise = null;
    this.pagesLoaded = false;
    this.loadFailed = false;
    this.query = '';
  }

  connectedCallback() {
    this.body = this.querySelector('[data-cl-body]');
    this.searchInput = this.querySelector('[data-cl-search]');
    this.countElement = this.querySelector('[data-cl-count]');
    this.emptyElement = this.querySelector('[data-cl-empty]');
    this.paginationElement = this.querySelector('[data-cl-pagination]');
    this.statusElement = this.querySelector('[data-cl-status]');
    this.zoomModal = this.querySelector('es-modal');
    if (!this.body) return;

    try {
      const stringsElement = this.querySelector('[data-cl-strings]');
      this.strings = stringsElement ? JSON.parse(stringsElement.textContent) : {};
    } catch (error) {
      this.strings = {};
    }

    this.total = parseInt(this.dataset.total, 10) || 0;
    this.currentPage = parseInt(this.body.dataset.currentPage, 10) || 1;
    this.pages = parseInt(this.body.dataset.pages, 10) || 1;
    // Rows fetched before a move in the DOM are still here; never fetch them twice.
    this.pagesLoaded = this.pages <= 1 || this.body.querySelector('[data-cl-extra]') !== null;
    this.initialCount = this.countElement ? this.countElement.textContent : '';
    this.abortController = new AbortController();

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearchInput);
      this.searchInput.addEventListener('keydown', this.onSearchKeyDown);
    }

    this.body.querySelectorAll('[data-cl-qty] input').forEach((input) => this.updateStepper(input));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.onSearchInput);
      this.searchInput.removeEventListener('keydown', this.onSearchKeyDown);
    }
    clearTimeout(this.searchTimer);
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    // An aborted load never finished: a reconnect (Theme Editor) starts it again on the next search.
    if (!this.pagesLoaded) this.pagesPromise = null;
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Delegated row controls
  --------------------------------------------------------------------------------------------------------------- */

  onClick(event) {
    const stepButton = event.target.closest('[data-cl-step]');
    if (stepButton && this.contains(stepButton)) {
      const input = stepButton.closest('[data-cl-qty]')?.querySelector('input');
      if (input) this.step(input, parseInt(stepButton.dataset.clStep, 10));
      return;
    }

    const addButton = event.target.closest('[data-cl-add]');
    if (addButton && this.contains(addButton)) {
      this.addToCart(addButton);
      return;
    }

    const zoomButton = event.target.closest('[data-cl-zoom]');
    if (zoomButton && this.contains(zoomButton)) {
      this.openZoom(zoomButton);
    }
  }

  onChange(event) {
    if (event.target.matches('[data-cl-qty] input')) {
      this.updateStepper(event.target, true);
    }
  }

  readLimits(input) {
    const min = parseInt(input.min, 10) || 1;
    const step = parseInt(input.step, 10) || 1;
    const max = input.max === '' ? Infinity : parseInt(input.max, 10);
    return { min, step, max: Number.isNaN(max) ? Infinity : max };
  }

  clamp(input, value) {
    const { min, step, max } = this.readLimits(input);
    // Snap to the step grid that starts at min, then keep within bounds.
    let next = min + Math.round((value - min) / step) * step;
    if (next > max) next = min + Math.floor((max - min) / step) * step;
    return Math.max(min, next);
  }

  step(input, direction) {
    const { step } = this.readLimits(input);
    const current = parseInt(input.value, 10) || 0;
    input.value = this.clamp(input, current + direction * step);
    this.updateStepper(input);
  }

  updateStepper(input, normalise = false) {
    if (normalise) input.value = this.clamp(input, parseInt(input.value, 10) || 0);
    const value = parseInt(input.value, 10) || 0;
    const { min, step, max } = this.readLimits(input);
    const wrapper = input.closest('[data-cl-qty]');
    if (!wrapper) return;
    const minus = wrapper.querySelector('[data-cl-step="-1"]');
    const plus = wrapper.querySelector('[data-cl-step="1"]');
    if (minus) minus.disabled = value <= min;
    if (plus) plus.disabled = value + step > max;
  }

  async addToCart(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId) return;

    const input = button.closest('tr')?.querySelector('[data-cl-qty] input');
    const quantity = input ? this.clamp(input, parseInt(input.value, 10) || 0) : 1;
    if (input) input.value = quantity;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      if (!window.CartAPI) throw new Error('CartAPI not available');
      await window.CartAPI.addLineItem(variantId, quantity);
      this.announce(this.strings.added || '');

      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (error) {
      // Shopify's own message (e.g. "only N left") is already in the shop's language.
      const message = (error && error.description) || this.strings.cartError || '';
      this.announce(message);
      if (message) window.alert(message);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  announce(message) {
    if (!this.statusElement) return;
    this.statusElement.textContent = '';
    // A fresh text node after clearing makes screen readers repeat an identical message.
    window.requestAnimationFrame(() => {
      if (this.statusElement) this.statusElement.textContent = message;
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Zoom
  --------------------------------------------------------------------------------------------------------------- */

  openZoom(button) {
    if (!this.zoomModal || typeof this.zoomModal.open !== 'function') return;
    const image = this.zoomModal.querySelector('[data-cl-zoom-image]');
    const title = this.zoomModal.querySelector('.modal__title');
    if (image) {
      image.src = button.dataset.zoomSrc;
      image.alt = button.dataset.zoomTitle || '';
    }
    if (title) title.textContent = button.dataset.zoomTitle || '';
    this.zoomModal.open(button);
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Search
  --------------------------------------------------------------------------------------------------------------- */

  onSearchKeyDown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    clearTimeout(this.searchTimer);
    this.search(this.searchInput.value);
  }

  onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.search(this.searchInput.value), CollectionListTable.SEARCH_DELAY);
  }

  async search(rawQuery) {
    this.query = rawQuery.trim().toLowerCase();

    if (!this.query) {
      this.reset();
      return;
    }

    if (!this.pagesLoaded && !this.pagesPromise) {
      this.pagesPromise = this.loadOtherPages();
    }
    // Filter what is already here at once; refine when the other pages arrive.
    this.filter();
    if (this.pagesPromise) {
      await this.pagesPromise;
      if (this.query) this.filter();
    }
  }

  filter() {
    let visible = 0;
    this.body.querySelectorAll('[data-cl-row]').forEach((row) => {
      const match = (row.dataset.search || '').includes(this.query);
      row.hidden = !match;
      if (match) visible += 1;
    });

    if (this.emptyElement) this.emptyElement.hidden = visible > 0;
    if (this.paginationElement) this.paginationElement.hidden = true;

    const results = (this.strings.results || '')
      .replace('[visible]', String(visible))
      .replace('[total]', String(this.total));
    let note = '';
    if (!this.pagesLoaded) {
      note = this.strings.loading || '';
    } else if (this.loadFailed) {
      note = this.strings.loadError || '';
    }
    this.setCount(`${results} ${note}`.trim());
  }

  reset() {
    this.body.querySelectorAll('[data-cl-row]').forEach((row) => {
      row.hidden = row.hasAttribute('data-cl-extra');
    });
    if (this.emptyElement) this.emptyElement.hidden = true;
    if (this.paginationElement) this.paginationElement.hidden = false;
    this.setCount(this.initialCount);
  }

  setCount(text) {
    if (this.countElement) this.countElement.textContent = text;
  }

  async loadOtherPages() {
    const sectionId = this.dataset.sectionId;
    const signal = this.abortController ? this.abortController.signal : undefined;
    const queue = [];
    for (let page = 1; page <= this.pages; page += 1) {
      if (page !== this.currentPage) queue.push(page);
    }

    // Rows are kept in page order, whatever order the responses arrive in.
    const pageRows = new Map();
    const worker = async () => {
      while (queue.length > 0) {
        const page = queue.shift();
        try {
          pageRows.set(page, await this.fetchPageRows(sectionId, page, signal));
        } catch (error) {
          if (signal && signal.aborted) return;
          this.loadFailed = true;
        }
      }
    };

    const workers = [];
    for (let i = 0; i < CollectionListTable.FETCH_CONCURRENCY; i += 1) workers.push(worker());
    await Promise.all(workers);
    if ((signal && signal.aborted) || !this.body) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    [...pageRows.keys()].sort((a, b) => a - b).forEach((page) => {
      const target = page < this.currentPage ? before : after;
      pageRows.get(page).forEach((row) => target.appendChild(row));
    });
    this.body.insertBefore(before, this.body.firstChild);
    this.body.appendChild(after);
    this.pagesLoaded = true;
  }

  async fetchPageRows(sectionId, page, signal) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Page ${page}: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...html.querySelectorAll('[data-cl-body] [data-cl-row]')].map((row) => {
      const imported = document.importNode(row, true);
      imported.setAttribute('data-cl-extra', '');
      imported.hidden = true;
      const input = imported.querySelector('[data-cl-qty] input');
      if (input) this.updateStepper(input);
      return imported;
    });
  }
}

if (!customElements.get('collection-list-table')) {
  customElements.define('collection-list-table', CollectionListTable);
}
